/**
 * Shared task utilities
 */
import { toast } from 'react-toastify';

const normalizeRawTicketNumber = (rawTicketNumber) => {
  if (rawTicketNumber === undefined || rawTicketNumber === null) {
    return '';
  }

  const normalizedTicketNumber = String(rawTicketNumber).trim();
  if (!normalizedTicketNumber) {
    return '';
  }

  // Subtask codes can include a breadcrumb-like parent path. We only need the current ticket number.
  return normalizedTicketNumber.includes('>')
    ? normalizedTicketNumber.split('>').pop().trim()
    : normalizedTicketNumber;
};

const normalizeFocusedCommentId = (focusedCommentId) => {
  if (focusedCommentId === undefined || focusedCommentId === null) {
    return '';
  }

  const normalizedCommentId = String(focusedCommentId).trim();
  if (!normalizedCommentId) {
    return '';
  }

  return normalizedCommentId;
};

export const buildTaskUrl = (task, focusedCommentId = null) => {
  const ticketNumber = getTaskTicketNumber(task);
  const commentId = normalizeFocusedCommentId(focusedCommentId);

  if (!ticketNumber) {
    return '';
  }

  const commentSegment = commentId ? `/focusedCommentId=${encodeURIComponent(commentId)}` : '';
  return `${window.location.origin}/task/${encodeURIComponent(ticketNumber)}${commentSegment}`;
};

export const getTaskTicketNumber = (...sources) => {
  for (const source of sources) {
    if (!source) continue;

    const rawTicketNumber = source.ticket_number ?? source.ticketNumber ?? source.code ?? source.number;
    const normalizedTicketNumber = normalizeRawTicketNumber(rawTicketNumber);
    if (!normalizedTicketNumber) continue;

    return normalizedTicketNumber;
  }

  return '';
};

export const formatTaskCode = (taskData, ticketDetails) => {
  return getTaskTicketNumber(ticketDetails, taskData) || 'N/A';
};

export const handleCopyTaskLink = (task, t, focusedCommentId = null) => {
  const taskUrl = buildTaskUrl(task, focusedCommentId);
  if (!taskUrl) {
    toast.error(t('taskViewModal.linkCopyFailed'));
    return;
  }

  const ticketNumber = getTaskTicketNumber(task);
  const linkText = `${task.title || 'Task'} - ${ticketNumber}`;
  
  // Copy URL for browser compatibility and rich text for other apps
  if (navigator.clipboard && window.ClipboardItem) {
    const htmlBlob = new Blob([`<a href="${taskUrl}">${linkText}</a>`], { type: 'text/html' });
    const textBlob = new Blob([taskUrl], { type: 'text/plain' });
    const clipboardItem = new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob
    });
    navigator.clipboard.write([clipboardItem]).then(() => {
      toast.success(t('taskViewModal.linkCopied'));
    }).catch(() => {
      // Fallback to URL only
      navigator.clipboard.writeText(taskUrl).then(() => {
        toast.success(t('taskViewModal.linkCopied'));
      }).catch(err => {
        toast.error(t('taskViewModal.linkCopyFailed'));
      });
    });
  } else {
    // Fallback for older browsers - copy URL
    navigator.clipboard.writeText(taskUrl).then(() => {
      toast.success(t('taskViewModal.linkCopied'));
    }).catch(err => {
      toast.error(t('taskViewModal.linkCopyFailed'));
    });
  }
};

export const handleCopyCommentLink = (task, t, commentId) => {
  const taskUrl = buildTaskUrl(task, commentId);
  if (!taskUrl) {
    toast.error(t('taskViewModal.commentLinkCopyFailed'));
    return;
  }

  const ticketNumber = getTaskTicketNumber(task);
  const linkText = `${task.title || 'Task'} - ${ticketNumber}`;

  if (navigator.clipboard && window.ClipboardItem) {
    const htmlBlob = new Blob([`<a href="${taskUrl}">${linkText}</a>`], { type: 'text/html' });
    const textBlob = new Blob([taskUrl], { type: 'text/plain' });
    const clipboardItem = new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob
    });
    navigator.clipboard.write([clipboardItem]).then(() => {
      toast.success(t('taskViewModal.commentLinkCopied'));
    }).catch(() => {
      navigator.clipboard.writeText(taskUrl).then(() => {
        toast.success(t('taskViewModal.commentLinkCopied'));
      }).catch(() => {
        toast.error(t('taskViewModal.commentLinkCopyFailed'));
      });
    });
  } else {
    navigator.clipboard.writeText(taskUrl).then(() => {
      toast.success(t('taskViewModal.commentLinkCopied'));
    }).catch(() => {
      toast.error(t('taskViewModal.commentLinkCopyFailed'));
    });
  }
};
